import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { Box, Typography } from "@mui/material";

import { ButtonV1 } from "components";
import { INTERNAL_FUND_TRANSFER } from "./constants";
import { ERROR_MSG } from "./tests/constants";
import ManualEntry from "./ManualEntry";
import { internalFundValidationSchema } from "./tests";
import { resetFieldMapper } from "./makeData";

const EditableTransaction = forwardRef(
  ({ data, errorList, isBulkUpload, onSubmit, onReset }, ref) => {
    const scrollToRef = useRef(null);

    const methods = useForm({
      mode: "onSubmit",
      resolver: yupResolver(internalFundValidationSchema),
    });

    const {
      reset,
      handleSubmit,
      trigger,
      getValues,
      setError,
      formState: { errors },
    } = methods;

    const handleResetForm = useCallback(() => {
      reset({ [INTERNAL_FUND_TRANSFER.yupName]: resetFieldMapper() });
      onReset();
    }, [reset, onReset]);

    const handleSave = useCallback(
      (data) => {
        const result = !data?.[INTERNAL_FUND_TRANSFER.yupName]
          ? data
          : getValues(INTERNAL_FUND_TRANSFER.yupName);

        onSubmit(result);
        handleResetForm();
      },
      [getValues, handleResetForm, onSubmit],
    );

    useImperativeHandle(ref, () => ({
      scroll() {
        window.scrollTo({
          top: scrollToRef.current.offsetTop,
          left: 0,
          behavior: "smooth",
        });
      },
    }));

    const setNewData = useCallback(() => {
      reset({ [INTERNAL_FUND_TRANSFER.yupName]: data }, { keepErrors: false });
    }, [data, reset]);

    const checkForError = useCallback(
      async (data) => {
        let hasError = false;

        if (isBulkUpload) {
          hasError = !(await trigger());
        } else if (errorList?.length) {
          errorList?.forEach((item, index) =>
            item?.forEach((error) => {
              setError(`${INTERNAL_FUND_TRANSFER.yupName}.${index}.${error}`, {
                message: ERROR_MSG[error],
              });
            }),
          );
          hasError = true;
        }

        if (isBulkUpload && !hasError) handleSave(data);
      },
      [isBulkUpload, errorList, handleSave, trigger, setError],
    );

    useEffect(() => {
      setNewData();
      void checkForError(data);
    }, [data]);
    return (
      <FormProvider {...methods}>
        <Typography marginTop={5}>
          {isBulkUpload || data?.[0]?.id || errorList?.length > 0
            ? `Review transactions (${data.length})`
            : "Manual upload"}
        </Typography>
        <Box
          ref={scrollToRef}
          as="form"
          onSubmit={handleSubmit(handleSave)}
          sx={{
            paddingY: 3,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {data?.map((item, index) => (
            <ManualEntry
              methods={methods}
              data={item}
              key={index}
              unique={index}
              errors={errors}
            />
          ))}
          <Box display={"flex"} justifyContent={"flex-end"} gap={3}>
            <ButtonV1
              variant="outlined"
              title="Clear all"
              onClick={handleResetForm}
            />
            <ButtonV1
              title="Save"
              color="primary"
              variant="contained"
              type={"submit"}
            />
          </Box>
        </Box>
      </FormProvider>
    );
  },
);

export default EditableTransaction;
